import type { WeeklyAttendanceEmployeeReport } from '../types/weeklyAttendanceReport';
import { parseCalendarDateInput, toCalendarDateString } from './dateTime';
import {
  businessToday,
  formatWeeklyHours,
  formatWeeklyStatusLabel,
} from './weeklyAttendanceReport';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const EMPLOYEE_OVERALL_WEEKLY_REPORT_CODE = 'EMPLOYEE_OVERALL_WEEKLY';

export type OverallWeeklyColumn = {
  date: string;
  weekday: string;
  dayLabel: string;
};

/**
 * Sunday-to-Saturday week containing the anchor date. The backend applies
 * the same rule (sundayWeekBounds), so the screen, Excel and PDF agree.
 */
export function overallWeekBounds(anchor: Date = businessToday()): {
  weekStart: string;
  weekEnd: string;
} {
  const sunday = new Date(
    anchor.getFullYear(),
    anchor.getMonth(),
    anchor.getDate() - anchor.getDay(),
    12,
  );
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  return {
    weekStart: toCalendarDateString(sunday),
    weekEnd: toCalendarDateString(saturday),
  };
}

/** Sunday-to-Saturday week deltaWeeks before / after the given week start. */
export function shiftOverallWeek(
  weekStart: string,
  deltaWeeks: number,
): { weekStart: string; weekEnd: string } {
  const start = parseCalendarDateInput(weekStart) ?? businessToday();
  start.setDate(start.getDate() + deltaWeeks * 7);
  return overallWeekBounds(start);
}

/**
 * Sunday–Saturday headings for the selected week, starting at weekStart
 * (a Sunday from overallWeekBounds).
 */
export function overallWeeklyColumns(weekStart: string): OverallWeeklyColumn[] {
  const start = parseCalendarDateInput(weekStart);
  if (!start) return [];
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      date: toCalendarDateString(date),
      weekday: WEEKDAYS[date.getDay()],
      dayLabel: `${date.getDate()} ${MONTHS[date.getMonth()]}`,
    };
  });
}

function todayCalendarDate(now = new Date()): string {
  return toCalendarDateString(businessToday(now));
}

/**
 * Same status text as the existing weekly and daily attendance screens.
 * Future dates show "-" instead of LOP (backend should already do this;
 * this is a display safeguard).
 */
export function overallWeeklyDayStatus(
  report: WeeklyAttendanceEmployeeReport,
  date: string,
  today: string = todayCalendarDate(),
): string {
  const day = report.days.find(item => item.date === date);
  if (!day) return '—';
  if (day.statusCode === 'FUTURE' || day.statusLabel === '-') return '-';
  if (date > today) {
    const label = formatWeeklyStatusLabel(day).trim().toLowerCase();
    const code = String(day.statusCode || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');
    const keep =
      code === 'HOLIDAY' ||
      code === 'WEEKEND' ||
      code === 'WEEKLY_OFF' ||
      code === 'ON_LEAVE' ||
      code === 'HALF_DAY_LEAVE' ||
      code === 'WFH' ||
      Boolean(day.arrivalTime) ||
      Boolean(day.isPresent) ||
      Boolean(day.isOnLeave) ||
      Boolean(day.isHoliday) ||
      Boolean(day.isWeeklyOff) ||
      Boolean(day.isWfh);
    if (!keep && (label.includes('lop') || label === 'absent' || !label || label === '—')) {
      return '-';
    }
  }
  const label = formatWeeklyStatusLabel(day).trim();
  return label || '—';
}

/**
 * Days the employee actually worked in the selected week (present share from
 * attendance). Holidays, weekly offs, leave-only days, and future dates are
 * excluded by the backend summary.
 */
export function overallWeeklyDaysWorked(
  report: WeeklyAttendanceEmployeeReport,
): string {
  const value =
    typeof report.summary.daysWorked === 'number'
      ? report.summary.daysWorked
      : report.summary.presentDays;
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : String(value);
}

function formatDayTotal(value: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return '0';
  }
  return String(Math.round(value * 100) / 100);
}

/**
 * Approved leave days in the week from the backend summary (half-day = 0.5).
 * Pending / rejected requests and Loss of Pay leave types are not included.
 */
export function overallWeeklyLeaveDays(
  report: WeeklyAttendanceEmployeeReport,
): string {
  return formatDayTotal(report.summary.totalLeaveDays);
}

/**
 * LOP days in the week from the backend summary: uncovered absence, approved
 * Loss of Pay leave and late-arrival LOP deductions.
 */
export function overallWeeklyLopDays(
  report: WeeklyAttendanceEmployeeReport,
): string {
  return formatDayTotal(report.summary.totalLopDays);
}

/**
 * Weekly total from the existing summary: net worked minutes for the week.
 * Break time, missing check-outs, approved leave, holidays, and weekly offs
 * are already excluded by that summary.
 */
export function overallWeeklyWorkHours(
  report: WeeklyAttendanceEmployeeReport,
): string {
  return formatWeeklyHours(report.summary.totalWorkedMinutes);
}

/** Flat rows for client-side export views that mirror the on-screen table. */
export function overallWeeklyExportRows(
  items: WeeklyAttendanceEmployeeReport[],
  weekStart: string,
  today?: string,
): Record<string, string>[] {
  const columns = overallWeeklyColumns(weekStart);
  return items.map(item => {
    const row: Record<string, string> = {
      Employee: item.employeeName,
      'Employee No': item.employeeNumber || '',
    };
    for (const column of columns) {
      row[column.date] = overallWeeklyDayStatus(item, column.date, today);
    }
    row['Total Working Days'] = overallWeeklyDaysWorked(item);
    row['Total Leave Days'] = overallWeeklyLeaveDays(item);
    row['Total LOP Days'] = overallWeeklyLopDays(item);
    row['Total Weekly Work Hours'] = overallWeeklyWorkHours(item);
    return row;
  });
}

export function overallWeeklyExportColumns(weekStart: string): string[] {
  return [
    'Employee',
    'Employee No',
    ...overallWeeklyColumns(weekStart).map(column => column.date),
    'Total Working Days',
    'Total Leave Days',
    'Total LOP Days',
    'Total Weekly Work Hours',
  ];
}
